import { motion } from 'framer-motion'
import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { CheckCircle } from 'lucide-react'
import { ModelViewer, resolveModelSource } from '../ModelViewer'
import { useModels } from '@/hooks/useModels'
import type { ShowcaseAnimation } from '@/types/camera'
import { migrateCameraState, cameraStateToQuaternion } from '@/utils/cameraConversion'
import { useCameraAnimation } from '@/utils/cameraAnimation'


interface TechnologyProps {
  /** 1-based index into the public model list (1 = first public model). */
  publicIndex?: number
}

export function Technology({ publicIndex = 4 }: TechnologyProps) {
  // Fetch public models (the API endpoint already filters by visibility=public).
  const { data: modelsData } = useModels({ sort: 'newest', limit: 20 })
  const models = modelsData?.data ?? []
  const selectedModel = models[Math.max(1, publicIndex) - 1] ?? null

  // Routing (single / ply / sequence / clipgstream) + URL derivation all live
  // in resolveModelSource; ModelViewer picks the right viewer from this.
  const source = useMemo(
    () => (selectedModel ? resolveModelSource(selectedModel) : null),
    [selectedModel?.id, selectedModel?.filePath, selectedModel?.metadata],
  )

  // SplatViewer extras (defaultCamera / modelTransform / autoFocus) parsed from metadata.
  const { defaultCamera, modelTransform, autoFocus } = useMemo(() => {
    if (!selectedModel?.metadata) {
      return { defaultCamera: undefined, modelTransform: undefined, autoFocus: true }
    }
    try {
      const meta = typeof selectedModel.metadata === 'string'
        ? JSON.parse(selectedModel.metadata)
        : selectedModel.metadata
      const camera = meta?.defaultCamera ? migrateCameraState(meta.defaultCamera) : undefined
      return { defaultCamera: camera, modelTransform: meta?.modelTransform, autoFocus: !camera }
    } catch {
      return { defaultCamera: undefined, modelTransform: undefined, autoFocus: true }
    }
  }, [selectedModel?.metadata])

  // Stable tuple for SplatSequencePlayer's external camera sync — recomputing
  // each render would re-fire the effect and override the user's view.
  const dualgsCameraProps = useMemo(() => {
    if (!defaultCamera) return null
    return cameraStateToQuaternion(defaultCamera)
  }, [defaultCamera])

  // Showcase animation from metadata (with keyframe migration to quaternion form)
  const showcaseAnimation = useMemo<ShowcaseAnimation | undefined>(() => {
    if (!selectedModel?.metadata) return undefined
    let meta: any = selectedModel.metadata
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta) } catch { return undefined }
    }
    const sa = meta?.showcaseAnimation
    if (!sa?.keyframes?.length) return undefined
    return {
      ...sa,
      // Landing-page showcase always loops, regardless of admin's saved setting.
      loop: true,
      keyframes: sa.keyframes.map((kf: any) => ({
        ...kf,
        camera: migrateCameraState(kf.camera)
      }))
    }
  }, [selectedModel?.metadata])

  const animationEnabled = !!showcaseAnimation
  // Drive both viewers from one external hook so loop/interrupt behavior is
  // unified. Bypasses SplatViewer's hover-based pauseAnimationOnInteraction.
  const useExternalAnimation = animationEnabled
  const {
    currentCamera: animatedCamera,
    currentTime,
    play: playAnimation,
    pause: pauseAnimation,
    reset: resetAnimation,
  } = useCameraAnimation(
    showcaseAnimation?.keyframes ?? [],
    showcaseAnimation?.duration ?? 0,
    showcaseAnimation?.loop ?? false,
    useExternalAnimation
  )

  // Synchronous gate so the next animation rAF can't race a state update; the
  // state mirror is what triggers the re-render that drops the camera prop.
  const userInterruptedRef = useRef(false)
  const [userInterrupted, setUserInterrupted] = useState(false)

  // Reset when the chosen model changes. The hook seeds isPlaying from
  // `enabled` only at mount, so we explicitly reset + play.
  useEffect(() => {
    userInterruptedRef.current = false
    setUserInterrupted(false)
    if (useExternalAnimation && selectedModel) {
      resetAnimation()
      playAnimation()
    }
    // resetAnimation/playAnimation are stable useCallbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel?.id, useExternalAnimation])

  // Pointer-down / wheel fires before OrbitControls processes the input, so we
  // can flip state in the same render the user's first move lands in.
  const handleViewerInteract = useCallback(() => {
    if (userInterruptedRef.current) return
    userInterruptedRef.current = true
    if (useExternalAnimation) pauseAnimation()
    setUserInterrupted(true)
  }, [useExternalAnimation, pauseAnimation])

  // Animated camera tuple — keyed on currentTime so paused state yields a stable
  // reference (viewer effect won't re-fire and overwrite the user's drag).
  const animatedCameraProps = useMemo(() => {
    if (!useExternalAnimation || userInterrupted) return null
    return cameraStateToQuaternion(animatedCamera)
    // animatedCamera identity changes each render; currentTime is the real driver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExternalAnimation, userInterrupted, currentTime])

  // After interaction we pass nothing so the viewer keeps the user's last view.
  // Otherwise: animation > static default.
  const activeCameraProps = userInterrupted
    ? null
    : (animatedCameraProps ?? dualgsCameraProps)

  const techStack = [
    {
      category: '3D Rendering',
      items: [
        'WebGL2 with custom shaders',
        'Gaussian Splatting algorithm',
        'Web Workers for parallel processing',
        'Progressive mesh loading',
      ],
    },
    {
      category: 'Performance',
      items: [
        'GPU-accelerated rendering',
        'Adaptive quality settings',
        'Chunk-based streaming',
        'Efficient memory management',
      ],
    },
    {
      category: 'Infrastructure',
      items: [
        'Cloudflare Workers edge computing',
        'Global CDN distribution',
        'R2 object storage',
        'D1 serverless database',
      ],
    },
    {
      category: 'Compatibility',
      items: [
        'Cross-browser support',
        'Mobile-optimized experience',
        'WebXR for VR/AR devices',
        'Progressive enhancement',
      ],
    },
  ]

  return (
    <section id="technology" className="py-20 lg:py-32 bg-gray-800/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Powered by{' '}
              <span className="bg-gradient-to-r from-primary-400 to-accent-500 bg-clip-text text-transparent">
                Advanced Technology
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-300 mb-8"
            >
              Our platform combines cutting-edge 3D rendering techniques with modern web technologies 
              to deliver an unparalleled volumetric viewing experience. Built on the revolutionary 
              splaTV engine, we bring photorealistic 4D content to your browser.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              {techStack.map((tech) => (
                <div key={tech.category}>
                  <h3 className="text-xl font-semibold text-white mb-3">{tech.category}</h3>
                  <ul className="space-y-2">
                    {tech.items.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div
              className="aspect-square rounded-3xl overflow-hidden bg-gray-900/40"
              onPointerDownCapture={handleViewerInteract}
              onMouseDownCapture={handleViewerInteract}
              onTouchStartCapture={handleViewerInteract}
              onWheelCapture={handleViewerInteract}
            >
              {source ? (
                <ModelViewer
                  source={source}
                  className="w-full h-full"
                  cameraPosition={activeCameraProps?.position ?? null}
                  cameraQuaternion={activeCameraProps?.quaternion ?? null}
                  onCameraChange={handleViewerInteract}
                  enableXR
                  dualgs={{ showFps: false, showUi: false, enableXR: true }}
                  splat={{
                    controls: true,
                    showUI: false,
                    showAxes: false,
                    autoRotate: !animationEnabled,
                    quality: 'high',
                    defaultCamera,
                    modelTransform,
                    autoFocus: autoFocus && !animationEnabled,
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                  No public model #{publicIndex}
                </div>
              )}
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent-500/20 rounded-full blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}